import React from 'react';

type Props = {
	height?: number;
	color?: string;
	dashLength?: number;
	gapLength?: number;
	direction?: 'horizontal' | 'vertical';
	length?: number | string;
	className?: string;
};

const DashedDivider: React.FC<Props> = ({
	height = 1,
	color = '#DADADA',
	dashLength = 8,
	gapLength = 8,
	direction = 'horizontal',
	length,
	className = '',
}) => {
	const ref = React.useRef<HTMLDivElement | null>(null);
	const isVertical = direction === 'vertical';

	React.useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const applyGradient = () => {
			const runLength = isVertical ? el.clientHeight : el.clientWidth;
			const segmentLength = dashLength + gapLength;
			if (runLength <= 0 || segmentLength <= 0) return;

			const base = Math.floor((runLength - dashLength) / segmentLength);
			const numSegments = Math.max(1, base);
			const adjustedGap =
				(runLength - dashLength - numSegments * dashLength) / numSegments;

			el.style.background = `repeating-linear-gradient(to ${
				isVertical ? 'bottom' : 'right'
			}, ${color} 0, ${color} ${dashLength}px, transparent ${dashLength}px, transparent ${
				dashLength + adjustedGap
			}px)`;
		};

		applyGradient();

		if (typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver(applyGradient);
			ro.observe(el);
			return () => ro.disconnect();
		}
	}, [color, dashLength, gapLength, isVertical]);

	const resolvedLength =
		typeof length === 'number' ? `${length}px` : length ?? '100%';

	const style: React.CSSProperties = isVertical
		? { width: `${height}px`, height: resolvedLength }
		: { height: `${height}px`, width: resolvedLength };

	return <div ref={ref} className={className} style={style} />;
};

export default DashedDivider;
